// ============================================================
// VetTech AI — Provider Abstraction Layer  v1.0  (Phase 0)
//
// BYOK / Privacy-first design:
//   • Keys stored in localStorage only, never on a server.
//   • Keys leave the device only as Authorization headers
//     to the user's chosen provider endpoint.
//   • Explicit opt-in; site is 100% functional with no key.
//   • Streaming always (SSE or NDJSON depending on provider).
//
// localStorage keys:
//   vt.ai.config  — { provider, apiKey, model, ollamaUrl }
//   vt.ai.usage   — { tokens: number, calls: number }
// ============================================================

const CONFIG_KEY = 'vt.ai.config';
const USAGE_KEY  = 'vt.ai.usage';

// ---- Provider catalogue ----

export const PROVIDERS = {
  openai: {
    label:        'OpenAI',
    requiresKey:  true,
    // GPT-5 era (Feb 2026+). gpt-4o, gpt-4.1, o4-mini retired Feb 13 2026.
    models: [
      'gpt-5.5',
      'gpt-5.4',
      'gpt-5.4-thinking',
      'gpt-5.3',
      'o3',
      'o3-mini',
      'gpt-4o-mini',
      'gpt-4.1-mini',
    ],
    defaultModel: 'gpt-4o-mini',
  },
  anthropic: {
    label:        'Anthropic',
    requiresKey:  true,
    // Active models only (Anthropic API names). Last verified May 2026.
    models: [
      'claude-opus-4-7',
      'claude-opus-4-6',
      'claude-opus-4-5-20251101',
      'claude-opus-4-1-20250805',
      'claude-sonnet-4-6',
      'claude-sonnet-4-5-20250929',
      'claude-haiku-4-5-20251001',
    ],
    defaultModel: 'claude-haiku-4-5-20251001',
  },
  gemini: {
    label:        'Google Gemini',
    requiresKey:  true,
    // Active models only. 1.5.x and 2.0 Flash discontinued; 3.x active.
    models: [
      'gemini-3.1-pro',
      'gemini-3-flash',
      'gemini-2.5-pro',
      'gemini-2.5-flash',
      'gemini-2.5-flash-lite',
    ],
    defaultModel: 'gemini-2.5-flash',
  },
  ollama: {
    label:        'Ollama (Local)',
    requiresKey:  false,
    models:       null,           // free-text model name
    defaultModel: 'llama3.1',
  },
};

// ---- Internal helpers ----

function safeJson(str, fallback) {
  if (str == null || str === '') return fallback;
  try {
    const parsed = JSON.parse(str);
    return parsed == null ? fallback : parsed;
  } catch { return fallback; }
}

// ---- Main AI object ----

export const AI = {

  // --- Config ---

  getConfig() {
    return safeJson(localStorage.getItem(CONFIG_KEY), {});
  },

  configure(patch) {
    const cfg = this.getConfig();
    localStorage.setItem(CONFIG_KEY, JSON.stringify({ ...cfg, ...patch }));
  },

  isConfigured() {
    const { provider, apiKey } = this.getConfig();
    if (!provider || !PROVIDERS[provider]) return false;
    return !PROVIDERS[provider].requiresKey || Boolean(apiKey);
  },

  // --- Usage tracking ---

  getUsage() {
    return safeJson(localStorage.getItem(USAGE_KEY), { tokens: 0, calls: 0 });
  },

  addUsage(tokens) {
    const u = this.getUsage();
    localStorage.setItem(USAGE_KEY, JSON.stringify({
      tokens: u.tokens + (tokens | 0),
      calls:  u.calls  + 1,
    }));
  },

  estimateTokens(text) {
    return Math.ceil((text || '').length / 4);
  },

  // --- Reset ---

  clearAll() {
    [CONFIG_KEY, USAGE_KEY].forEach(k => localStorage.removeItem(k));
  },

  // ---------------------------------------------------------------
  // chat({ system, messages, onToken, onDone, onError })
  //
  //   system   — system prompt string
  //   messages — [{ role: 'user'|'assistant', content: string }]
  //   onToken  — called with each streamed text chunk
  //   onDone   — called when stream ends (no argument)
  //   onError  — called with error message string
  // ---------------------------------------------------------------
  async chat({ system = '', messages = [], onToken, onDone, onError } = {}) {
    const cfg = this.getConfig();
    if (!cfg.provider) {
      onError?.('No provider configured. Open AI Settings to add your API key.');
      return;
    }

    const adapters = {
      openai:    () => this._openai(cfg, system, messages, onToken, onDone),
      anthropic: () => this._anthropic(cfg, system, messages, onToken, onDone),
      gemini:    () => this._gemini(cfg, system, messages, onToken, onDone),
      ollama:    () => this._ollama(cfg, system, messages, onToken, onDone),
    };

    try {
      const fn = adapters[cfg.provider];
      if (!fn) throw new Error(`Unknown provider: ${cfg.provider}`);
      await fn();
    } catch (e) {
      onError?.(e.message || String(e));
    }
  },

  // ---- OpenAI adapter (chat completions, SSE) ----

  async _openai(cfg, system, messages, onToken, onDone) {
    const resp = await fetch('https://api.openai.com/v1/chat/completions', {
      method:  'POST',
      headers: {
        'Content-Type':  'application/json',
        'Authorization': `Bearer ${cfg.apiKey}`,
      },
      body: JSON.stringify({
        model:    cfg.model || 'gpt-4o-mini',
        stream:   true,
        messages: [{ role: 'system', content: system }, ...messages],
      }),
    });
    await this._assertOk(resp);

    let full = '';
    await this._streamSSE(resp, data => {
      if (data === '[DONE]') return;
      const tok = safeJson(data, {})?.choices?.[0]?.delta?.content || '';
      if (tok) { onToken?.(tok); full += tok; }
    });
    this.addUsage(this.estimateTokens(full));
    onDone?.();
  },

  // ---- Anthropic adapter (messages API, SSE) ----

  async _anthropic(cfg, system, messages, onToken, onDone) {
    const resp = await fetch('https://api.anthropic.com/v1/messages', {
      method:  'POST',
      headers: {
        'Content-Type':                          'application/json',
        'x-api-key':                             cfg.apiKey,
        'anthropic-version':                     '2023-06-01',
        'anthropic-dangerous-direct-browser-calls': 'true',
      },
      body: JSON.stringify({
        model:      cfg.model || 'claude-haiku-3-5',
        max_tokens: 1024,
        stream:     true,
        system,
        messages,
      }),
    });
    await this._assertOk(resp);

    let full = '';
    await this._streamSSE(resp, data => {
      const evt = safeJson(data, {});
      if (evt.type === 'content_block_delta') {
        const tok = evt.delta?.text || '';
        if (tok) { onToken?.(tok); full += tok; }
      }
    });
    this.addUsage(this.estimateTokens(full));
    onDone?.();
  },

  // ---- Gemini adapter (generateContent, SSE via ?alt=sse) ----

  async _gemini(cfg, system, messages, onToken, onDone) {
    const model = cfg.model || 'gemini-2.0-flash';
    const url   = `https://generativelanguage.googleapis.com/v1beta/models/${model}:streamGenerateContent?alt=sse&key=${cfg.apiKey}`;

    // Gemini uses 'model' role instead of 'assistant'
    const contents = messages.map(m => ({
      role:  m.role === 'assistant' ? 'model' : 'user',
      parts: [{ text: m.content }],
    }));

    const resp = await fetch(url, {
      method:  'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        systemInstruction: { parts: [{ text: system }] },
        contents,
        generationConfig:  { maxOutputTokens: 1024 },
      }),
    });
    await this._assertOk(resp);

    let full = '';
    await this._streamSSE(resp, data => {
      const tok = safeJson(data, {})?.candidates?.[0]?.content?.parts?.[0]?.text || '';
      if (tok) { onToken?.(tok); full += tok; }
    });
    this.addUsage(this.estimateTokens(full));
    onDone?.();
  },

  // ---- Ollama adapter (chat, NDJSON streaming) ----

  async _ollama(cfg, system, messages, onToken, onDone) {
    const base = (cfg.ollamaUrl || 'http://localhost:11434').replace(/\/$/, '');
    const resp = await fetch(`${base}/api/chat`, {
      method:  'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        model:    cfg.model || 'llama3.1',
        stream:   true,
        messages: [{ role: 'system', content: system }, ...messages],
      }),
    });
    if (!resp.ok) throw new Error(`Ollama: ${resp.status} ${resp.statusText}`);

    // Ollama streams NDJSON (newline-delimited JSON), not SSE
    const reader  = resp.body.getReader();
    const decoder = new TextDecoder();
    let full = '';

    while (true) {
      const { done, value } = await reader.read();
      if (done) break;
      for (const line of decoder.decode(value).split('\n')) {
        if (!line.trim()) continue;
        const evt = safeJson(line, {});
        const tok = evt.message?.content || '';
        if (tok) { onToken?.(tok); full += tok; }
        if (evt.done) {
          this.addUsage(this.estimateTokens(full));
          onDone?.();
          return;
        }
      }
    }
    this.addUsage(this.estimateTokens(full));
    onDone?.();
  },

  // ---- Shared: assert HTTP 2xx ----

  async _assertOk(resp) {
    if (resp.ok) return;
    let msg = resp.statusText;
    try {
      const body = await resp.clone().json();
      msg = body?.error?.message || body?.message || msg;
    } catch { /* ignore parse errors */ }
    throw new Error(`[${resp.status}] ${msg}`);
  },

  // ---- Shared: consume SSE stream, call onData for each data line ----
  // Buffers across chunk boundaries to handle partial lines correctly.

  async _streamSSE(resp, onData) {
    const reader  = resp.body.getReader();
    const decoder = new TextDecoder();
    let buffer = '';

    while (true) {
      const { done, value } = await reader.read();
      if (done) break;
      buffer += decoder.decode(value, { stream: true });
      const lines = buffer.split('\n');
      buffer = lines.pop() ?? '';
      for (const line of lines) {
        const trimmed = line.trimEnd();
        if (trimmed.startsWith('data: ')) {
          onData(trimmed.slice(6));
        }
      }
    }
    // flush any remaining buffer
    if (buffer.trimEnd().startsWith('data: ')) {
      onData(buffer.trimEnd().slice(6));
    }
  },

  // ---------------------------------------------------------------
  // streamInto(onUpdate, { system, prompt, onDone, onError })
  //
  // Convenience wrapper around chat() for React state streaming.
  // Accumulates tokens and calls onUpdate(fullText) after each token,
  // so you can pass a React setState setter directly.
  //
  //   onUpdate — (fullText: string) => void  (e.g. setExplanation)
  //   system   — system prompt string
  //   prompt   — single user message string
  //   onDone   — optional () => void
  //   onError  — optional (msg: string) => void
  //
  // Returns a Promise that resolves when streaming is complete.
  //
  // Usage:
  //   const [text, setText] = useState('');
  //   AI.streamInto(setText, { system: '…', prompt: 'Explain X.' });
  // ---------------------------------------------------------------
  streamInto(onUpdate, { system = '', prompt = '', onDone, onError } = {}) {
    let accumulated = '';
    return this.chat({
      system,
      messages: [{ role: 'user', content: prompt }],
      onToken: (tok) => {
        accumulated += tok;
        onUpdate?.(accumulated);
      },
      onDone: () => onDone?.(),
      onError: (err) => onError?.(err),
    });
  },
};
